import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TenantProvider } from '@/lib/entitlements';
import { ThemeProvider, useTheme } from '@/lib/theme';
import { AppErrorBoundary } from '@/app/app-error-boundary';
import { createQueryClient } from '@/app/query-client';
import { router } from '@/app/routes';
import { installApi } from '@/lib/api/bootstrap';
import '@/styles/theme.css';

const queryClient = createQueryClient();

// Nothing can be toasted before the user acts, so the toaster arrives just
// after the first paint instead of inside it.
const Toaster = lazy(() => import('sonner').then((module) => ({ default: module.Toaster })));

/** Sonner paints its own surface, so it needs to be told which theme won. */
function AppToaster() {
  const { resolvedTheme } = useTheme();
  return (
    <Toaster
      theme={resolvedTheme}
      position="bottom-right"
      gap={8}
      toastOptions={{
        classNames: {
          toast: 'rounded-lg border border-border bg-popover text-popover-foreground shadow-lg',
          description: 'text-muted-foreground',
        },
      }}
    />
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('Falta el nodo #root en index.html.');

// Install the backend before the first render so no provider races an empty
// client. Dynamic, so the seed data stays out of the entry chunk.
const { mode, apiBaseUrl } = await installApi();
if (mode === 'mock') {
  // Silence would make "why is my data fake?" a debugging session.
  console.info('[ribox-crm] Sin API configurada: corriendo sobre datos simulados.');
} else {
  console.info(`[ribox-crm] API: ${apiBaseUrl}`);
}

createRoot(container).render(
  <StrictMode>
    <AppErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <TenantProvider>
              <RouterProvider router={router} />
              <Suspense fallback={null}>
                <AppToaster />
              </Suspense>
            </TenantProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
